import { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import HC_CONSTANTS from 'HC_CONSTANTS';
import SVGInline from 'react-svg-inline';

import {
  MAP_CONTROL_TYPE_HEATMAP,
  MAPS_DEFAULT_ZOOM_MAX
} from 'legacy/appstore/constants';

import { areAnyPropsChanged } from 'legacy/utils/component';
import { buildClusterIcon } from 'legacy/utils/maps';

import {
  fetchUserLocationViaIP,
  Maps,
  LayersControl,
  LayersControlGroup,
  LayersControlItem,
  Legend,
  LegendSchools,
  Baselayer,
  BaselayersControl,
  Marker,
  MarkerCluster,
  PullFromLeafletContainer
} from 'hcmaps-leaflet';

import MapControlClustersContainer from 'legacy/containers/map-control-clusters';

import MapControlToggleButtons from 'legacy/components/MapControlToggleButtons';
import SVGInlineWithRelativePath from 'legacy/components/generic/SVGInlineWithRelativePath';
import MapDrawControls from 'legacy/components/MapDrawControls';
import MapDrawFeatureGroup from 'legacy/components/MapDrawFeatureGroup';

import growthIcon from 'legacy/assets/svg-inline/growth.svg';
import schoolIcon from 'legacy/assets/svg-inline/school.svg';
import priceIcon from 'legacy/assets/svg-inline/price.svg';
import medianIcon from 'legacy/assets/svg-inline/median.svg';
import censusIcon from 'legacy/assets/svg-inline/census.svg';
import rentalYieldIcon from 'legacy/assets/svg-inline/rental-yield.svg';
import layerLoadingOutline from 'legacy/assets/svg-inline/map-layer-loading.svg';

const { MAPS } = HC_CONSTANTS;
const BASELAYERS = [
  {
    isDefaultActive: true,
    url: MAPS.PRIMARY.FULL,
    minimapBaselayer: {
      label: 'Street',
      labelStyle: {
        color: 'black',
        textShadow: '0px 0px 2px white'
      },
      url: MAPS.PRIMARY.MINI
    }
  },
  {
    url: MAPS.SECONDARY.FULL,
    minimapBaselayer: {
      label: 'Satellite',
      labelStyle: {
        color: 'white',
        textShadow: '0px 0px 2px black'
      },
      url: MAPS.SECONDARY.MINI
    }
  }
];

const LAYER_DESCRIPTIONS = {
  priceSqFt: 'Find the average price/square foot of each local block ',
  growth: 'Discover the historical price growth of every block in your area',
  median: 'See the average block-level price of homes in your area',
  schools:
    'Identify the school district and rating for every home in your area',
  census: 'Locate the census tract boundaries for your city'
};

// Keep in sync with CSS animation timing
const CONTROLS_ANIMATION_DURATION = 500;
const REFERENCE_PROPERTY_LEGEND_INCREMENT = 0.1;
const MOBILE_CONTROLS_HEIGHT = 100;
const _getDefaultFitMapPadding = (isShowingMobileControls) => ({
  topLeft: [50, 75],
  // Map controls consume a portion of the bottom of the map area
  bottomRight: [50, isShowingMobileControls ? MOBILE_CONTROLS_HEIGHT : 50]
});

// Leaflet moves our layers control into its own DOM hierarchy.  Let's move it back.
let InlineLayersControl = PullFromLeafletContainer(LayersControl);

export default class MapsComponent extends Component {
  static propTypes = {
    LayersControlWrapper: PropTypes.func,
    markers: PropTypes.array
  };

  static defaultProps = {
    markers: [],
    fitMarkersOnMarkerChange: true,
    showToggleControls: true,
    maxZoom: MAPS_DEFAULT_ZOOM_MAX
  };

  state = {
    activeLayerGroup: null,
    isShowingLayerGroupDescription: false,
    activeLayerIds: []
  };

  scrollTimeout = null;

  // Use matchMedia listener to center/fit the map before printing
  fitMapBeforePrint = (mql) => {
    if (mql.matches) {
      this.fitMap();
    }
  };

  // Sometimes we don't want to programmatically activate a layer until the map is ready
  setActiveLayerIdPromise = new Promise((resolve) => resolve());

  componentDidMount() {
    if (this.props.areControlsToggled && this.props.areControlsToggleable) {
      window.clearTimeout(this.fitMapAfterMountTimeout);
      this.waitForControlsToggle();
    }
    if (this.props.activeLayerIds && this.props.activeLayerIds.length) {
      this.activateLayers();
    }

    window.matchMedia('print').addListener(this.fitMapBeforePrint);
  }

  componentWillUnmount() {
    window.matchMedia('print').removeListener(this.fitMapBeforePrint);
    window.clearTimeout(this.fitMapAfterMountTimeout);
    window.clearTimeout(this.controlsAnimationLayerActivateTimeout);
    window.clearTimeout(this.controlsAnimationFitMapTimeout);
    window.clearTimeout(this.fitMapTriggerTimeout);
  }

  componentDidUpdate(prevProps, prevState) {
    // Map controls were toggled either programmatically or by user, wait for show/hide
    // animation to complete before re-centering map
    if (
      prevProps.areControlsToggled !== this.props.areControlsToggled &&
      this.props.areControlsToggleable
    ) {
      this.waitForControlsToggle();
    }
    // Only set `activeLayerIds` once, when first loaded. After this, active layers
    // will be controlled within the component
    if (
      (!prevProps.activeLayerIds ||
        (prevProps.activeLayerIds && !prevProps.activeLayerIds.length)) &&
      this.props.activeLayerIds &&
      this.props.activeLayerIds.length
    ) {
      this.activateLayers();
    }
    // When triggered, allow time for page transitions to finish before refitting
    if (this.props.fitMapTrigger !== prevProps.fitMapTrigger) {
      window.clearTimeout(this.fitMapTriggerTimeout);
      this.fitMapTriggerTimeout = window.setTimeout(
        this.fitMap,
        CONTROLS_ANIMATION_DURATION
      );
    }
  }

  // Prevent map from re-rendering unnecessarily often
  shouldComponentUpdate(nextProps, nextState) {
    const propsList = [
      'className',
      'markers',
      'referenceProperty',
      'isHidingControls',
      'isHidingBaselayerControl',
      'areControlsToggleable',
      'areControlsToggled',
      'disableScrollZoom',
      'disableDragging',
      'fitMapTrigger',
      'fullWidthLegend',
      'resetMapLayers',
      'activeLayerIds',
      'visibleControlsType',
      'drawControlsVisible',
      'drawControlTypeActive',
      'drawLayerPolygons',
      'drawAlwaysEditable',
      'showLoginPromptForDraw',
      'clustersDisabled',
      'children'
    ];
    return (
      areAnyPropsChanged(propsList, this.props, nextProps) ||
      areAnyPropsChanged(Object.keys(this.state), this.state, nextState)
    );
  }

  // Only fit the map view and remotely activate layers after the map has re-centered itself following
  // the controls appearing (animation)
  waitForControlsToggle = () => {
    this.setActiveLayerIdPromise = new Promise((resolve) => {
      this.controlsAnimationLayerActivateTimeout = window.setTimeout(
        resolve,
        CONTROLS_ANIMATION_DURATION
      );
    });
    this.controlsAnimationFitMapTimeout = window.setTimeout(
      this.fitMap,
      CONTROLS_ANIMATION_DURATION
    );
  };

  // Activate map layers after ensuring that the map view is set
  activateLayers = () => {
    this.fitMap();
    this.mapApi.getMap().whenReady(() => {
      this.setActiveLayerIdPromise.then(() => {
        this.setState({ activeLayerIds: this.props.activeLayerIds });
      });
    });
  };

  fitMap = () => {
    if (this.mapApi && this.mapApi.getIsReadyToPosition()) {
      this.mapApi.getMap().invalidateSize();
      // removing this as QA expressed concern about losing previous zoom level
      // this.mapApi.goHome();
    }
  };

  handleShowGroupDescription = (e) => {
    if (this.state.activeLayerGroup) {
      this.setState({
        isShowingLayerGroupDescription:
          !this.state.isShowingLayerGroupDescription
      });
    }
  };

  render() {
    /* If a new property is added here that should cause the map to change in any way
     * it must also be added to the list in `shouldComponentUpdate` above
     */
    const {
      children,
      token,
      className,
      markers,
      getMarkerPopup,
      handleLayerControlsClick,
      handleMapLayersChange,
      referenceProperty,
      onMarkerClick,
      toastShow,
      isHidingControls,
      isHidingBaselayerControl,
      areControlsToggleable,
      areControlsToggled,
      disableScrollZoom,
      disableDragging,
      fitMapPadding,
      initialZoom,
      maxZoom,
      onShowMapControlsClick,
      showControlsClickActionLayers,
      showControlsClickActionDraw,
      LayersControlWrapper,
      hasLargeBaselayerControl,
      resetMapLayers,
      fullWidthLegend,
      useRentalGaiaEndpoint,
      dataHcName,
      drawEnabled,
      visibleControlsType,
      drawControlsVisible,
      drawControlTypeActive,
      drawControlCallbacks,
      drawLayerPolygons,
      drawAlwaysEditable,
      fitMarkersOnMarkerChange,
      disableDrawControls,
      onMarkerPopupClick,
      showLoginPromptForDraw,
      locationControlOnly,
      subjectLocationChangable,
      clustersDisabled,
      showToggleControls,
      showClusterControl,
      qaSelectorSuffix
    } = this.props;
    // TODO remove this in favor of a more idiomatic method of resetting map layers
    if (resetMapLayers) {
      InlineLayersControl = PullFromLeafletContainer(LayersControl);
    }
    // Broken out to support adding this inline or inside of a passed-in presentational wrapper component
    const LayersControlPartial = () => (
      <InlineLayersControl
        layout={fullWidthLegend ? 'full' : 'small'}
        defaultActiveLayerGroups={this.state.activeLayerIds}
        onActiveLayerGroupChange={(activeGroups) => {
          this.setState({ activeLayerGroup: activeGroups[0] });
          if (handleMapLayersChange) {
            handleMapLayersChange(
              activeGroups.map((component) => component.props.uId)
            );
          }
          if (!activeGroups.length) {
            this.setState({ isShowingLayerGroupDescription: false });
          }
        }}
        StatusMessage={
          <div className="map-active-group-description-wrapper">
            <div
              onClick={this.handleShowGroupDescription}
              className={classNames(
                'map-active-group-description-toggle material-icons',
                {
                  shown: !isHidingControls && this.state.activeLayerGroup
                }
              )}
            >
              {this.state.isShowingLayerGroupDescription
                ? 'expand_more'
                : 'expand_less'}
            </div>
            <div
              className={classNames('map-active-group-description', {
                shown:
                  this.state.isShowingLayerGroupDescription &&
                  this.state.activeLayerGroup
              })}
            >
              <div>
                {this.state.activeLayerGroup &&
                  this.state.activeLayerGroup.props.description}
              </div>
            </div>
          </div>
        }
        LoadingIcon={<SVGInlineWithRelativePath svg={layerLoadingOutline} />}
      >
        <LayersControlGroup
          uId="median"
          data-hc-name="average"
          description={LAYER_DESCRIPTIONS.median}
          label="Average $"
          Icon={<SVGInline svg={medianIcon} />}
          isMutuallyExclusive
        >
          {useRentalGaiaEndpoint ? (
            <LayersControlItem
              typePreset="blocks"
              useRentalEndpoint
              metric="price_average"
            />
          ) : (
            <LayersControlItem typePreset="blocks" metric="price_mean" />
          )}

          <Legend
            legendBreakIncrement={
              referenceProperty &&
              referenceProperty.priceMean &&
              REFERENCE_PROPERTY_LEGEND_INCREMENT
            }
          />
        </LayersControlGroup>

        {
          // hide non-rental $ heat-map layers when in rental mode (note: replace w/ gross yield.)
          !useRentalGaiaEndpoint && (
            <LayersControlGroup
              uId="growth"
              data-hc-name="growth"
              description={LAYER_DESCRIPTIONS.growth}
              label="Historical $ Growth"
              Icon={<SVGInline svg={growthIcon} />}
              isMutuallyExclusive
            >
              <LayersControlItem
                typePreset="blocks"
                label={
                  <span>
                    <span className="label-emphasis">1</span> Year
                  </span>
                }
                metric="price_pctchg_1_yr"
                isDefaultActive
              />
              <LayersControlItem
                typePreset="blocks"
                label={
                  <span>
                    <span className="label-emphasis">5</span> Year
                  </span>
                }
                metric="price_pctchg_5_yr"
              />
              <Legend />
            </LayersControlGroup>
          )
        }

        {
          // hide non-rental $ heat-map layers when in rental mode
          !useRentalGaiaEndpoint && (
            <LayersControlGroup
              uId="priceSqFt"
              data-hc-name="ppsf"
              description={LAYER_DESCRIPTIONS.priceSqFt}
              label="Price/Sq.Ft."
              Icon={<SVGInline svg={priceIcon} />}
              isMutuallyExclusive
            >
              <LayersControlItem typePreset="blocks" metric="prices" />
              <Legend
                legendBreakIncrement={
                  referenceProperty &&
                  referenceProperty.prices &&
                  REFERENCE_PROPERTY_LEGEND_INCREMENT
                }
              />
            </LayersControlGroup>
          )
        }

        <LayersControlGroup
          uId="schools"
          data-hc-name="schools"
          description={LAYER_DESCRIPTIONS.schools}
          label="Schools"
          isMutuallyExclusive
          Icon={<SVGInline svg={schoolIcon} />}
        >
          <LayersControlItem
            label="Elementary"
            typePreset="schools"
            isDefaultActive
            metric="schools_elementary"
            featureFilter={function (layerInfo, feature) {
              return feature.properties.level === 'P';
            }}
          />
          <LayersControlItem
            label="Middle"
            typePreset="schools"
            metric="schools_middle"
            featureFilter={function (layerInfo, feature) {
              return feature.properties.level === 'M';
            }}
          />
          <LayersControlItem
            label="High"
            typePreset="schools"
            metric="schools_high"
            featureFilter={function (layerInfo, feature) {
              return feature.properties.level === 'H';
            }}
          />
          <LegendSchools />
        </LayersControlGroup>

        {useRentalGaiaEndpoint ? (
          <LayersControlGroup
            uId="rental-yield"
            data-hc-name="rental-yield"
            description={LAYER_DESCRIPTIONS.rentalYield}
            label="Rental Yield"
            isMutuallyExclusive
            Icon={<SVGInline svg={rentalYieldIcon} />}
          >
            <LayersControlItem
              useRentalEndpoint
              isPercentBased
              metric="gross_yield_average"
              typePreset="blocks"
            />
            <Legend
              legendBreakIncrement={
                referenceProperty &&
                referenceProperty.grossYieldAverage &&
                REFERENCE_PROPERTY_LEGEND_INCREMENT
              }
            />
          </LayersControlGroup>
        ) : (
          <LayersControlGroup
            uId="census"
            data-hc-name="census"
            description={LAYER_DESCRIPTIONS.census}
            label="Census"
            isMutuallyExclusive
            Icon={<SVGInline svg={censusIcon} />}
          >
            <LayersControlItem typePreset="tracts" />
          </LayersControlGroup>
        )}
      </InlineLayersControl>
    );
    return (
      <Maps
        geotileApiUrl={HC_CONSTANTS.GEOJSON_DATA_URL}
        apiToken={token}
        resolveFallbackLocation={() =>
          fetchUserLocationViaIP(HC_CONSTANTS.GEO_LOCATION_URL, {
            'hc-auth-token': token
          })
        }
        notificationCallback={toastShow}
        referenceProperty={referenceProperty}
        fitMarkersOnMarkerChange={fitMarkersOnMarkerChange}
        leafletOptions={{
          scrollWheelZoom: !disableScrollZoom,
          dragging: !disableDragging,
          maxZoom: maxZoom,
          zoom: initialZoom,
          editable: drawEnabled
        }}
        fitMapPadding={
          fitMapPadding || _getDefaultFitMapPadding(!isHidingControls)
        }
        afterMount={(api) => {
          // This required for the search page map to behave itself when new map
          // instances render immediately after changing container size in mobile Safari
          this.mapApi = api;
          this.fitMapAfterMountTimeout = window.setTimeout(this.fitMap, 100);
        }}
        className={classNames('map-container', className, {
          'with-full-width-legend': fullWidthLegend,
          'with-large-baselayer-control': hasLargeBaselayerControl,
          'with-controls-sidepanel': areControlsToggled,
          'map-draw map-draw--edit': drawEnabled && drawAlwaysEditable,
          [`map-draw map-draw--${drawControlTypeActive}`]:
            !!drawControlTypeActive
        })}
        data-hc-name={dataHcName}
      >
        {/* Setting `zoomToBoundsOnClick=true` when disabling dragging (as on small mobile map) to fix MarkerCluster bug */}
        <MarkerCluster
          useForInitialMapBounds
          disabled={clustersDisabled}
          disabledZoomLevel={maxZoom}
          maxClusterRadius={(zoom) => (zoom === maxZoom ? 0 : zoom * 2.75)}
          iconCreateFunction={buildClusterIcon}
          zoomToBoundsOnClick={!!disableDragging}
        >
          {
            // Markers with falsy `lat` or `lng` objects will be `undefined`, filter them out
            markers
              .filter((marker) => marker)
              .map((marker) => {
                const { leafletOptions = {}, events = {} } = marker;
                return (
                  <Marker
                    key={marker.uId}
                    position={[marker.lat, marker.lon]}
                    useForInitialMapBounds
                    leafletOptions={{
                      zIndexOffset: marker.zIndexOffset,
                      riseOnHover: true,
                      riseOffset: 1001,
                      ...leafletOptions
                    }}
                    iconConfig={marker.iconConfig}
                    labelForHtmlIcon={marker.labelHtml}
                    excludeFromCluster={marker.excludeFromCluster}
                    onClick={(api) =>
                      onMarkerClick && onMarkerClick(api, marker)
                    }
                    Popup={
                      getMarkerPopup
                        ? getMarkerPopup(marker, onMarkerPopupClick) ||
                          undefined
                        : undefined
                    }
                    {...events}
                  />
                );
              })
          }
        </MarkerCluster>
        {drawLayerPolygons && drawLayerPolygons.length && (
          <MapDrawFeatureGroup
            layers={drawLayerPolygons.filter((p) => !!p)}
            editable={
              drawLayerPolygons.length &&
              (drawAlwaysEditable || drawControlTypeActive === 'edit')
            }
            inDeleteMode={drawControlTypeActive === 'delete'}
          />
        )}
        <div
          className={classNames({
            hidden:
              isHidingControls ||
              visibleControlsType !== MAP_CONTROL_TYPE_HEATMAP
          })}
        >
          {LayersControlWrapper
            ? // Calling as function as opposed to using JSX in order to minimize unnecessary remounting
              LayersControlWrapper({
                LayerControlsComponent: LayersControlPartial(),
                onClick: handleLayerControlsClick
              })
            : LayersControlPartial()}
        </div>
        <MapDrawControls
          subjectLocationChangable={subjectLocationChangable}
          polygons={drawLayerPolygons || []}
          activeControlType={drawControlTypeActive}
          isVisible={!isHidingControls && drawControlsVisible}
          disableControls={disableDrawControls}
          locationControlOnly={locationControlOnly}
          showLoginPromptForDraw={showLoginPromptForDraw}
          {...drawControlCallbacks}
        />
        {isHidingControls || isHidingBaselayerControl ? (
          <Baselayer url={BASELAYERS[0].url} />
        ) : (
          <BaselayersControl baseLayers={BASELAYERS} />
        )}
        {showToggleControls && (
          <MapControlToggleButtons
            qaSelectorSuffix={qaSelectorSuffix}
            showControlLayers={isHidingControls || areControlsToggleable}
            showControlDraw={drawEnabled && !drawAlwaysEditable}
            onShowControlsClickLayers={() => {
              onShowMapControlsClick(showControlsClickActionLayers);
            }}
            onShowControlsClickDraw={() => {
              onShowMapControlsClick(showControlsClickActionDraw);
            }}
          />
        )}
        {showClusterControl && <MapControlClustersContainer />}
        {
          // Render additional layers, etc. passed-in by HOCs
          children
        }
      </Maps>
    );
  }
}
